import type { ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

import { AlertIcon, CheckIcon, InfoIcon } from './icons';

/**
 * A short message with a colour and an icon: what the reader has to know before going
 * on. `role="alert"` only when it is a warning or a danger, which is when a screen reader
 * should be interrupted.
 *
 * `compact` is the one-line version for the state of the system («Modo: solo propone ·
 * Último correo: hace 3 min»): title and text on the same line, less padding, 13.5 px.
 * It is for a fact that sits above a list, not for something the reader has to act on.
 */
type Tone = 'info' | 'warn' | 'danger' | 'ok' | 'neutral';

const STYLES: Record<Tone, string> = {
  info: 'bg-info-soft text-info border-info/20',
  warn: 'bg-warn-soft text-warn border-warn/25',
  danger: 'bg-danger-soft text-danger border-danger/25',
  ok: 'bg-ok-soft text-ok border-ok/25',
  neutral: 'bg-paper-2 text-ink-2 border-line',
};

const ICONS: Record<Tone, ReactNode> = {
  info: <InfoIcon size={18} />,
  warn: <AlertIcon size={18} />,
  danger: <AlertIcon size={18} />,
  ok: <CheckIcon size={18} />,
  neutral: <InfoIcon size={18} />,
};

const COMPACT_ICONS: Record<Tone, ReactNode> = {
  info: <InfoIcon size={15} />,
  warn: <AlertIcon size={15} />,
  danger: <AlertIcon size={15} />,
  ok: <CheckIcon size={15} />,
  neutral: <InfoIcon size={15} />,
};

export function Callout({
  tone = 'info',
  title,
  action,
  compact = false,
  className,
  children,
  role,
}: {
  tone?: Tone;
  title?: ReactNode;
  /** A button on the right: the way out of what the message says. */
  action?: ReactNode;
  /** One line, `py-2`, 13.5 px: for the state of the system, not for a warning to act on. */
  compact?: boolean;
  className?: string;
  children?: ReactNode;
  role?: 'alert' | 'status';
}) {
  const inferredRole = role ?? (tone === 'warn' || tone === 'danger' ? 'alert' : undefined);

  if (compact) {
    return (
      <div
        role={inferredRole}
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-[13.5px] leading-snug',
          STYLES[tone],
          className,
        )}
      >
        <span className="shrink-0" aria-hidden="true">
          {COMPACT_ICONS[tone]}
        </span>
        <div className="min-w-0 flex-1">
          {title ? <span className="font-semibold">{title}</span> : null}
          {title && children ? ' ' : null}
          {children}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    );
  }

  return (
    <div
      role={inferredRole}
      className={cn(
        'flex flex-wrap items-start gap-3 rounded-lg border px-4 py-3 text-[14px]',
        STYLES[tone],
        className,
      )}
    >
      <span className="mt-0.5 shrink-0" aria-hidden="true">
        {ICONS[tone]}
      </span>
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title ? 'mt-0.5' : null)}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
